// Page0
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  List,
  ListItem,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

function Page0({
  schedules,
  user,
  onDeleteSchedule,
  onDuplicateSchedule,
  schedulesLoading,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [sortBy, setSortBy] = useState("dateDesc");

  const handleAddSchedule = useCallback(() => {
    navigate("/new");
  }, [navigate]);

  const handleEdit = useCallback(
    (schedule) => {
      navigate("/edit", {
        state: { schedule, title: schedule.title || "" },
      });
    },
    [navigate],
  );

  const handleDelete = useCallback(
    async (scheduleId, scheduleName) => {
      if (
        window.confirm(
          t("deleteConfirmation", { scheduleName: `"${scheduleName}"` }),
        )
      ) {
        if (onDeleteSchedule) {
          try {
            await onDeleteSchedule(scheduleId);
          } catch (error) {
            console.error("Error deleting schedule:", error);
          }
        } else {
          console.error("onDeleteSchedule function not provided");
        }
      }
    },
    [onDeleteSchedule, t],
  );

  const handleDuplicate = useCallback(
    async (schedule) => {
      if (onDuplicateSchedule) {
        try {
          const duplicatedSchedule = {
            ...schedule,
            title: `New ${schedule.title || t("schedule")}`,
            createdAt: new Date(),
            updatedAt: new Date(),
            id: undefined, // Remove the ID so a new one gets generated
          };
          await onDuplicateSchedule(duplicatedSchedule);
        } catch (error) {
          console.error("Error duplicating schedule:", error);
        }
      } else {
        console.error("onDuplicateSchedule function not provided");
      }
    },
    [onDuplicateSchedule, t],
  );

  const formatDate = useCallback(
    (date) => {
      if (!date) return t("unknownDate");
      const d = date instanceof Date ? date : new Date(date);
      return format(d, "PPP");
    },
    [t],
  );

  // Helper function to determine if schedule was updated
  const wasScheduleUpdated = useCallback((schedule) => {
    if (!schedule.updatedAt || !schedule.createdAt) return false;

    const created =
      schedule.createdAt instanceof Date
        ? schedule.createdAt
        : new Date(schedule.createdAt);
    const updated =
      schedule.updatedAt instanceof Date
        ? schedule.updatedAt
        : new Date(schedule.updatedAt);

    // Consider it updated if the difference is more than 1 minute
    return Math.abs(updated.getTime() - created.getTime()) > 60000;
  }, []);

  const sortedSchedules = useMemo(() => {
    if (!schedules) return [];
    const sorted = [...schedules];
    if (sortBy === "nameAsc") {
      sorted.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    } else if (sortBy === "dateDesc") {
      sorted.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }
    return sorted;
  }, [schedules, sortBy]);

  const memoizedSchedulesList = useMemo(() => {
    if (!sortedSchedules || sortedSchedules.length === 0) {
      return (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            border: "2px dashed #ccc",
            borderRadius: "8px",
            color: "text.secondary",
          }}
        >
          <Typography variant="h6" gutterBottom>
            {t("noSchedulesYet")}
          </Typography>
          <Typography variant="body2">{t("noSchedulesDescription")}</Typography>
        </Box>
      );
    }

    return (
      <List>
        {sortedSchedules.map((schedule) => {
          const isUpdated = wasScheduleUpdated(schedule);

          return (
            <ListItem
              key={schedule.id}
              sx={{
                mb: 1,
                border: "1px solid #eee",
                borderRadius: "8px",
                p: { xs: 2, sm: 2 },
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: { xs: "flex-start", sm: "space-between" },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  mb={1}
                >
                  <Typography variant="h6" component="span">
                    {schedule.title ||
                      `${t("schedule")} - ${formatDate(schedule.date)}`}
                  </Typography>
                  {schedule.summary && (
                    <Chip
                      label={`${schedule.summary.totalShifts} ${t("shifts")}`}
                      size="small"
                      color="primary"
                    />
                  )}
                </Stack>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="textSecondary">
                    {isUpdated
                      ? `${t("updated")}: ${formatDate(schedule.updatedAt)}`
                      : `${t("created")}: ${formatDate(schedule.createdAt)}`}
                  </Typography>
                  {schedule.summary && (
                    <Typography variant="body2" color="textSecondary">
                      {schedule.summary.totalPeople} {t("people")} ×{" "}
                      {schedule.numDays} {t("days")}
                    </Typography>
                  )}
                </Stack>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                mt={{ xs: 2, sm: 0 }}
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}
              >
                <IconButton
                  color="primary"
                  onClick={() => handleEdit(schedule)}
                  title={t("editSchedule")}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color="secondary"
                  onClick={() => handleDuplicate(schedule)}
                  title={t("duplicateSchedule")}
                >
                  <ContentCopyIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() =>
                    handleDelete(
                      schedule.id,
                      schedule.title ||
                        `${t("schedule")} - ${formatDate(schedule.date)}`,
                    )
                  }
                  title={t("deleteSchedule")}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </ListItem>
          );
        })}
      </List>
    );
  }, [
    sortedSchedules,
    t,
    handleEdit,
    handleDuplicate,
    handleDelete,
    formatDate,
    wasScheduleUpdated,
  ]);

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 4 },
        maxWidth: "600px",
        mx: "auto",
        backgroundColor: "#F5F5F5",
        borderRadius: "12px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ mb: { xs: 1, sm: 0 } }}>
          {t("welcome")}, {user?.displayName || user?.email}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t("userId")}: {user?.uid?.slice(0, 8)}...
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          size="large"
          startIcon={<AddCircleOutlineIcon />}
          sx={{
            px: { xs: 2, sm: 4 },
            py: { xs: 1, sm: 1.5 },
            fontSize: "1.1rem",
            borderRadius: "12px",
            boxShadow: 3,
            textTransform: "none",
          }}
          onClick={handleAddSchedule}
        >
          {t("addNewSchedule")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          {t("yourSchedules")} ({sortedSchedules.length})
        </Typography>
        <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
          <InputLabel>{t("sortBy")}</InputLabel>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            label={t("sortBy")}
          >
            <MenuItem value="dateDesc">{t("sort by Date")}</MenuItem>
            <MenuItem value="nameAsc">{t("sort by Title")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {schedulesLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{t("loadingSchedules")}</Typography>
        </Box>
      ) : (
        memoizedSchedulesList
      )}
    </Box>
  );
}

export default Page0;
